/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, HostListener, inject, OnDestroy, reflectComponentType } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Params, Router, UrlSegment } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil, throttleTime } from 'rxjs';
import { BaseService } from '../services/base.service';
import { UtilDialogService } from '../services/dialog.service';
import { AuthUser } from '../services/auth.service';
import { IHasSuspendables, ISuspendable } from './component-interfaces';
import { ProximityRightEnum } from '@models';
import { AgFns, GridOptionsExt } from './ag-grid';
import { RowSelectedEvent } from '@ag-grid-community/core';
import { IEntityWithStatus, IStatusResult, StatusService } from '../services/status.service';
import { HelpService } from '../services/help.service';

export function wrapBusy(target, key, descriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = async function (...args) {
    // wrapBusy might be on the component or the component's 'parent'.
    const wrapBusy = (this.wrapBusy && this.wrapBusy.bind(this)) ?? (this.parent?.wrapBusy && this.parent.wrapBusy.bind(parent));
    if (wrapBusy) {
      return await wrapBusy(async () => await originalMethod.apply(this, args));
    } else {
      return await originalMethod.apply(this, args);
    }
  };
}

@Component({
  selector: 'prox-base',
  template: '',
  standalone: false,
})
export abstract class BaseComponent implements OnDestroy, IHasSuspendables {
  /** Use `.pipe(takeUntil(this.destroy$))` on all observables */
  destroy$ = new Subject<boolean>();
  dialogService: UtilDialogService;

  router: Router;
  toastr: ToastrService;
  urlSegments: UrlSegment[] = [];
  isPageReady = false;
  //isFormDisabled = false;
  authUser?: AuthUser;
  suspendables = new Set<ISuspendable>();
  ProximityRightEnum = ProximityRightEnum;
  /** Id of currently active entity or entity in selected grid row */
  entityId?: string;
  selectedRow?: any;
  statusServiceBase!: StatusService;
  statusResult?: IStatusResult;
  public busyPromise: Promise<any> | undefined;
  private helpService!: HelpService;
  protected isDestroyed = false;

  constructor(
    public baseService: BaseService,
    public route: ActivatedRoute,
  ) {
    this.statusServiceBase = inject(StatusService);
    this.helpService = inject(HelpService);
    this.dialogService = baseService.dialogService;
    this.router = baseService.router;
    this.toastr = baseService.toastr;
    this.authUser = baseService.authService.getUser();
    // this.setTitle("");

    // TODO: think about when we use queryParamMap instead of paramMap...

    // setTimeout is critical here because injection of the derived class values does NOT occur until after the base class ctor completes.
    // so any refs to injected values in the 'updateFromParams' call below will be null without this.
    setTimeout(() => this.init(), 0);
  }

  protected async init() {
    this.route.url.pipe(takeUntil(this.destroy$), throttleTime(100)).subscribe(async () => {
      //this.statusResult = undefined;
      const params = this.collectRouteParams(this.router);
      // console.log("  unified params:  " + JSON.stringify(params));

      await this.updateFromParams(params);

      // Register help handler if this component has a showHelp method
      if (this.hasMethod('showHelp')) {
        this.helpService.registerHelp(() => (this as any).showHelp());
      }
    });
  }

  addSuspendable(suspendable: ISuspendable) {
    this.suspendables.add(suspendable);
  }

  /** Return true if user has any of the rights */
  hasRight(...rights: ProximityRightEnum[]) {
    return this.authUser && this.authUser.hasRight(...rights);
  }

  public isBusy() {
    // DON'T add this.busyService.isBusy() here, it will cause ExpressionChangedAfterItHasBeenCheckedError in some cases.
    return this.busyPromise != null || this['dbSaveService']?.isBusy();
  }

  async wrapBusy<T>(op: Promise<T> | (() => Promise<T>)) {
    // if already busy - just await the operation without setting busy again
    if (this.busyPromise) {
      return await (typeof op === 'function' ? op() : op);
    }

    try {
      this.busyPromise = typeof op === 'function' ? op() : op;
      return await this.busyPromise;
    } finally {
      this.busyPromise = undefined;
    }
  }

  /** Get working status for given entity.  Cached by component until updateFromParams() is called again.  */
  getWorkingStatus(entity: IEntityWithStatus | any, isApiEnabled = false): IStatusResult {
    if (!this.statusResult) {
      this.statusResult = this.statusServiceBase.getWorkingStatus(entity, isApiEnabled);
    }
    return this.statusResult;
  }

  /** Collect the route params from all active routes and url segments */
  protected collectRouteParams(router: Router) {
    let params = {};
    const stack: ActivatedRouteSnapshot[] = [router.routerState.snapshot.root];
    while (stack.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const route = stack.pop()!;
      const urlParams = this.collectUrlParams(route.url);
      params = { ...params, ...urlParams, ...route.params };
      stack.push(...route.children);
    }
    return <Params>params;
  }

  /** Get all parameters defined on the url segments.  In case of name collision, child route value wins over parent.
   * Note: For optional parameters, use this instead of route.params because route.params get removed and replaced
   * when child routes also have optional parameters.
   */
  private collectUrlParams(urlSegments: UrlSegment[]) {
    let params = {};
    for (const seg of urlSegments) {
      params = { ...params, ...seg.parameters };
    }
    return params;
  }

  /** Initalize the component with current route params.  Generally used in place of ngOnInit */
  abstract updateFromParams(params?: object): void | Promise<void>;

  onRowSelectedDefault(e: RowSelectedEvent) {
    if (!e.node.isSelected()) return;
    const gridOptions = e.context.gridOptions;
    const keyField = e.context.gridState.keyField || 'id';
    this.updateGridRouteParams(gridOptions, e.data[keyField]);
    this.selectedRow = e.data;
  }

  updateGridRouteParams(gridOptions: GridOptionsExt, key?: string) {
    const urlNoQp = this.router.url.split('?')[0];
    const urlTree = this.router.createUrlTree([urlNoQp], {});
    const url = AgFns.buildGridRouteParamsUrl(urlTree, gridOptions, key);
    this.entityId = key;
    this.baseService.location.replaceState(url);
  }

  /** Stop navigation if something is pending */
  public async canDeactivate() {
    if (this.busyPromise) {
      await this.busyPromise;
    }

    if (this['dbSaveService']?.busyPromise) {
      await this['dbSaveService'].busyPromise;
    }

    return true;
  }

  // No longer needed - use wrapBusy
  // public guardDeactivate<T>(promise: Promise<T>): Promise<T> {
  //   if (this.pendingOp) {
  //     // There's already an higher level save in progress, so we don't keep the nested one
  //     return promise;
  //   }

  //   this.pendingOp = promise;
  //   return promise.finally(() => {
  //     this.pendingOp = undefined;
  //   });
  // }

  /** Stop window closing if there are unsaved changes - cannot be async */
  @HostListener('window:beforeunload')
  public canUnload() {
    // will be overloaded later
    return true;
  }

  /** Discard unsaved changes and return to previous page */
  // TODO: Later we should consider making this abstract
  public deactivate() {
    this.isPageReady = false;
    // should be overriden
  }

  hasMethod(name: string) {
    return name in this;
  }

  /** Navigate back to previous or parent page */
  navigateBack() {
    this.baseService.location.back();
    // this.router.navigate(['..'], { relativeTo: this.route });
  }

  /** Terminate observables.  Be sure to call super.ngOnDestroy() if overridden. */
  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.destroy$.next(true);
    // Teardown (discard unsaved changes, clear query caches, reset isPageReady)
    // runs here — when the component is genuinely destroyed — rather than from
    // canDeactivateGuard, which only checks whether leaving is allowed and may
    // run for a navigation the router subsequently aborts.
    this.deactivate();
    // Unregister help handler when component is destroyed
    if (this.hasMethod('showHelp')) {
      this.helpService.unregisterHelp();
    }
  }

  /** Set the page title.  Ignored once the component is torn down: updateFromParams() is
   * async and is NOT cancelled by destruction, so a page we have already navigated away
   * from must not stamp its stale title over the incoming page. */
  setTitle(title: string) {
    if (this.isDestroyed) {
      return;
    }
    // let ttl = 'Proximity System';
    // if (title.length > 0) {
    //   ttl += ' - ' + title;
    // }
    this.baseService.pageTitleService.setPageTitle(title);
  }

  /** Return the component's selector, e.g. 'prox-account-info' */
  get selector(): string {
    const metadata = reflectComponentType(this.constructor as any);
    return metadata?.selector || this.constructor.name;
  }

  async wrapError<T>(opDescription: string, op: Promise<T> | (() => Promise<T>)) {
    try {
      return await this.wrapBusy(op);
    } catch (e) {
      const ex = e as any;
      await this.dialogService.warnDialog(opDescription, ex.message ?? ex.toString());
      return null;
    }
  }
}
